import { ImageIcon } from 'lucide-react';
import { useState } from 'react';

import { ComposerDecision } from '../composer-gate-context';
import { CompactPhotoPicker } from './compact-photo-picker';
import { DecisionFooter } from './decision-footer';

interface AssetReferenceCandidate {
  id: string;
  name: string;
  thumbnailUrl?: string | null;
  blobUrl?: string | null;
}

interface AssetReferenceCardProps {
  id?: string;
  /** The owner's own words for the upload they meant. */
  assetRef: string;
  candidates: AssetReferenceCandidate[];
  /**
   * Cards answer by SENDING A MESSAGE. There is no tool-output channel back
   * from a card — the model reads the reply and calls the tool again, this
   * time with a concrete asset id.
   */
  onSendMessage: (message: string) => void;
}

/**
 * "Which of these did you mean?"
 *
 * The tool refuses to guess which upload the owner meant, which is right. What
 * was wrong is how it asked: a sentence listing FILENAMES — "I found
 * 'IMG_2841.jpg', 'endo-3.jpg'. Which one should I use?" — and a wait for one
 * to be typed back. Nobody knows their photos by filename. The thumbnails were
 * already loaded two files away in `ImagePickerDialog`; this shows them.
 *
 * Single-select on purpose: the question is which ONE asset was meant, not
 * which set to work from.
 */
export function AssetReferenceCard({
  id,
  assetRef,
  candidates,
  onSendMessage,
}: AssetReferenceCardProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const [resolved, setResolved] = useState(false);
  const selected = candidates.find((candidate) => candidate.id === selectedId);
  const respond = (message: string) => {
    if (resolved) return;
    setResolved(true);
    onSendMessage(message);
  };
  const accept = () => {
    if (selected) respond(`Use this one: ${selected.name} (${selected.id}).`);
  };
  const reject = () => respond('None of those — just generate a new one.');
  const composerGate =
    !resolved && candidates.length > 0
      ? {
          sentence: `Which one did you mean by “${assetRef}”?`,
          onAccept: accept,
          onReject: reject,
          acceptLabel: 'Use this one',
          rejectLabel: 'Generate a new one',
          onEdit: () => setPickerOpen(true),
          editLabel: 'Choose a photo',
          acceptDisabled: !selected,
          acceptDisabledReason: 'Choose a photo first.',
        }
      : null;

  // Nothing matched. Saying so beats an empty grid, and the way forward is a
  // sentence rather than a control — the owner either renames, uploads, or
  // tells Claire to generate one.
  if (candidates.length === 0) {
    return (
      <div
        data-testid="claire-card-asset-reference"
        className="w-full rounded-lg border bg-card p-4 sm:max-w-lg"
      >
        <div className="flex items-center gap-2 text-sm font-medium">
          <ImageIcon className="size-4 text-muted-foreground" />
          No match for “{assetRef}”
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Nothing in your library matches that name. Upload it, or ask for a new
          one to be generated instead.
        </p>
      </div>
    );
  }

  if (resolved) {
    return <p className="text-xs text-muted-foreground">Selection recorded.</p>;
  }

  return (
    <ComposerDecision
      id={id ?? `asset-reference:${assetRef}`}
      gate={composerGate}
    >
      <div
        data-testid="claire-card-asset-reference"
        className="w-full rounded-lg border bg-card p-4 sm:max-w-lg"
      >
        <div className="flex items-center gap-2 text-sm font-medium">
          <ImageIcon className="size-4 text-muted-foreground" />
          Which one did you mean by “{assetRef}”?
        </div>

        <div className="mt-3 space-y-2">
          <CompactPhotoPicker
            previewCandidates
            assets={candidates}
            selectedIds={selectedId ? [selectedId] : []}
            maxCount={1}
            disabled={resolved}
            showTrigger={false}
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            onConfirm={(ids) => {
              const candidate = candidates.find((entry) => entry.id === ids[0]);
              if (candidate) setSelectedId(candidate.id);
            }}
          />
        </div>

        <div className="mt-4">
          <DecisionFooter
            hideSentence
            sentence={`Which one did you mean by “${assetRef}”?`}
            onAccept={accept}
            onReject={reject}
            onEdit={() => setPickerOpen(true)}
            editLabel="Choose a photo"
            acceptLabel="Use this one"
            rejectLabel="Generate a new one"
            acceptDisabled={!selected}
            acceptDisabledReason="Choose a photo first."
          />
        </div>
      </div>
    </ComposerDecision>
  );
}
